/**
 * Virtual Operator
 * Represents a simulated warehouse operator performing tasks
 */

import {
  VirtualOperatorInfo,
  OperatorStatus,
  OperatorSpecialization,
  WorkflowType
} from '../types';

export interface VirtualOperatorConfig {
  id: string;
  name: string;
  specialization: OperatorSpecialization;
}

export class VirtualOperator {
  private readonly id: string;
  private readonly name: string;
  private readonly specialization: OperatorSpecialization;
  private status: OperatorStatus = 'idle';
  private currentTask: string | undefined;
  private currentWorkflow: WorkflowType | undefined;
  private tasksCompleted: number = 0;
  private errors: number = 0;
  private isActive: boolean = true;

  constructor(config: VirtualOperatorConfig) {
    this.id = config.id;
    this.name = config.name;
    this.specialization = config.specialization;
  }

  /**
   * Get operator ID
   */
  getId(): string {
    return this.id;
  }

  /**
   * Get operator name
   */
  getName(): string {
    return this.name;
  }

  /**
   * Get operator specialization
   */
  getSpecialization(): OperatorSpecialization {
    return this.specialization;
  }

  /**
   * Check if operator is available for a task
   */
  isAvailable(): boolean {
    return this.isActive && this.status === 'idle';
  }

  /**
   * Check if operator can handle a specific workflow
   */
  canHandle(workflowType: WorkflowType): boolean {
    if (!this.isAvailable()) return false;

    // General operators can handle anything
    if (this.specialization === 'general') return true;

    // Specialized operators prefer their specialty but can handle others
    return true;
  }

  /**
   * Start a task
   */
  startTask(workflowType: WorkflowType, taskDescription: string): void {
    this.status = 'busy';
    this.currentWorkflow = workflowType;
    this.currentTask = taskDescription;
  }

  /**
   * Complete current task successfully
   */
  completeTask(): void {
    this.status = 'idle';
    this.currentWorkflow = undefined;
    this.currentTask = undefined;
    this.tasksCompleted++;
  }

  /**
   * Mark current task as failed
   */
  failTask(errorMessage?: string): void {
    this.status = 'error';
    this.errors++;

    // Reset to idle after a brief moment
    setTimeout(() => {
      if (this.isActive) {
        this.status = 'idle';
        this.currentWorkflow = undefined;
        this.currentTask = undefined;
      }
    }, 1000);
  }

  /**
   * Release operator back to idle state
   */
  release(): void {
    this.status = 'idle';
    this.currentWorkflow = undefined;
    this.currentTask = undefined;
  }

  /**
   * Stop the operator
   */
  stop(): void {
    this.isActive = false;
    this.status = 'idle';
    this.currentWorkflow = undefined;
    this.currentTask = undefined;
  }

  /**
   * Reactivate the operator
   */
  activate(): void {
    this.isActive = true;
    this.status = 'idle';
  }

  /**
   * Get operator info for display
   */
  getInfo(): VirtualOperatorInfo {
    return {
      id: this.id,
      name: this.name,
      status: this.status,
      specialization: this.specialization,
      currentTask: this.currentTask,
      currentWorkflow: this.currentWorkflow,
      tasksCompleted: this.tasksCompleted,
      errors: this.errors
    };
  }

  /**
   * Reset operator statistics
   */
  resetStats(): void {
    this.tasksCompleted = 0;
    this.errors = 0;
  }
}

/**
 * Operator Factory
 * Creates virtual operators with appropriate specializations
 */
export class VirtualOperatorFactory {
  private static nextId: number = 1;

  /**
   * Create a set of operators with balanced specializations.
   *
   * @param namedOperators Valgfri liste med ekte operatør-ID/navn (demo-modus,
   *   B1-B2-C7). Når satt brukes disse i rekkefølge slik at API-kallenes
   *   operatorId matcher seedede operatører i tenanten (leaderboard viser da
   *   ekte navn). Er count større enn lista, fylles resten med genererte
   *   SIM-OP-operatører som før. Utelatt = eksakt samme oppførsel som før.
   */
  static createOperators(
    count: number,
    namedOperators?: { id: string; name: string }[]
  ): VirtualOperator[] {
    const operators: VirtualOperator[] = [];
    const specializations: OperatorSpecialization[] = [
      'picking',
      'packing',
      'receiving',
      'putaway',
      'general',
      'general'
    ];

    for (let i = 0; i < count; i++) {
      const specialization = specializations[i % specializations.length];
      const named = namedOperators && i < namedOperators.length ? namedOperators[i] : undefined;
      operators.push(
        new VirtualOperator({
          id: named ? named.id : `SIM-OP-${this.nextId++}`,
          name: named ? named.name : `Simulator Operator ${i + 1}`,
          specialization
        })
      );
    }

    return operators;
  }

  /**
   * Reset the ID counter
   */
  static reset(): void {
    this.nextId = 1;
  }
}
